import type { DialogProps } from "@mui/material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { FC, PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DialogCloseIcon } from "../DialogCloseIcon/DialogCloseIcon";

export interface DeleteConfirmDialogProps extends PropsWithChildren, Omit<DialogProps, "title"> {
  /**
   * Callback to be called when the user confirms the dialog.
   */
  onConfirm: () => void;
  /**
   * Callback to be called when the user cancels the dialog.
   */
  onCancel: () => void;
  /**
   * Title of the dialog.
   */
  title?: ReactNode;
}

/**
 * Dialog with confirm and cancel buttons. Use for confirmation dialog messages
 */
export const DeleteConfirmDialog: FC<DeleteConfirmDialogProps> = ({
  onConfirm,
  onCancel,
  open,
  title,
  children,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <Dialog onClose={onCancel} open={open} {...props}>
      <DialogTitle sx={{ minHeight: "80px" }}>
        {title ?? t("confirmDelete")}
        <DialogCloseIcon onClick={onCancel} />
      </DialogTitle>
      <DialogContent>{children ?? t("deleteConfirmationMessage")}</DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t("cancel")}</Button>
        <Button color="error" data-cy="deleteConfirmDialogConfirmButton" onClick={onConfirm} variant="contained">
          {t("delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
